import {
  Action,
  AssignmentInstance,
  DueInstance,
  FormSubmission,
  isActiveStepInstanceStatus,
  NonEmptyArray,
  reviveAssignmentInstance,
  reviveDueInstance,
  reviveFormSubmission,
  reviveStepInstanceStatus,
  reviveStepNote,
  StepDefinitionId,
  StepInstanceId,
  StepInstanceStatus,
  StepNote,
  Temporal,
} from '@willow/types-iso';

import { StepDefinition, StepType, StepTypeMap } from '../definitions/steps/StepDefinition';
import { Expression } from '../Expressions/AbstractExpressionEvaluator';
import { WorkflowExpressionEvaluator as E } from '../Expressions/WorkflowExpressionEvaluator';
import { DisplayInfoItem, migrateDisplayInfo, SerializedStepInstance } from './SerializedStepInstance';

/**
 * HydratedStepInstance is a thin wrapper around SerializedStepInstance that
 * adds some convenience methods for computed fields.

 * It should be used in a readonly manner. It can be used frontend or backend.
 * 
 * For backend use, especially uses that involve computing new states,
 * @see AbstractStepInstance in shared-node instead.
 */
export class HydratedStepInstance<T extends StepType> implements SerializedStepInstance<T> {
  public readonly id: StepInstanceId;

  public readonly stepDefinitionId: StepDefinitionId;

  public readonly type: T;

  public readonly definition: StepTypeMap[T];

  public readonly statusHistory: NonEmptyArray<StepInstanceStatus>;

  public readonly formSubmissions: FormSubmission[];

  public readonly dueAt: DueInstance;

  public readonly retryCondition: Expression<'boolean'> | undefined;

  public readonly isVisible: boolean;

  public readonly willActivateOn: Temporal.ZonedDateTime | undefined;

  public readonly canManuallyActivate: boolean;

  public readonly canManuallySkip: boolean;

  public readonly canManuallyComplete: boolean;

  public readonly canManuallyFail: boolean;

  public readonly canEditAfterCompletion: boolean | undefined;

  public readonly actionsToTake: Action[];

  public readonly assignmentHistory: AssignmentInstance[];

  public readonly notes: StepNote[];

  public readonly displayInfo: DisplayInfoItem[];

  public readonly preallocatedEntityRefs: Record<string, string>;

  constructor(
    public readonly stepInstance: SerializedStepInstance<T>,
    definition: StepTypeMap[T],
  ) {
    this.id = stepInstance.id;
    this.stepDefinitionId = stepInstance.stepDefinitionId;
    this.type = stepInstance.type;
    this.definition = StepDefinition.parse(definition) as StepTypeMap[T];
    this.statusHistory = stepInstance.statusHistory.map(reviveStepInstanceStatus) as NonEmptyArray<StepInstanceStatus>;
    this.formSubmissions = stepInstance.formSubmissions.map(reviveFormSubmission);
    this.dueAt = reviveDueInstance(stepInstance.dueAt);
    this.retryCondition = stepInstance.retryCondition;
    this.isVisible = stepInstance.isVisible;
    this.canManuallyActivate = stepInstance.canManuallyActivate;
    this.canManuallySkip = stepInstance.canManuallySkip;
    this.canManuallyComplete = stepInstance.canManuallyComplete;
    this.canManuallyFail = stepInstance.canManuallyFail;
    this.canEditAfterCompletion = stepInstance.canEditAfterCompletion;
    this.willActivateOn = stepInstance.willActivateOn
      ? Temporal.ZonedDateTime.from(stepInstance.willActivateOn)
      : undefined;
    this.assignmentHistory = stepInstance.assignmentHistory.map(reviveAssignmentInstance);
    this.notes = stepInstance.notes.map(reviveStepNote);
    this.displayInfo = migrateDisplayInfo(stepInstance.displayInfo);
    this.actionsToTake = stepInstance.actionsToTake;
    this.preallocatedEntityRefs = stepInstance.preallocatedEntityRefs ?? {};
  }

  /**
   * If a retry condition is present we need to combine it with the automatically
   * activate condition so that we can activate the step after the retry duration
   * has elapsed.
   */
  get automaticallyActivateCondition(): Expression<'boolean'> {
    if (this.retryCondition == null) {
      return this.definition.automaticallyActivate;
    }
    return E.boolean`and(${E.nest(this.definition.automaticallyActivate)}, ${E.nest(this.retryCondition)})`;
  }

  get automaticallyCompleteCondition(): Expression<'boolean'> {
    return this.definition.automaticallyComplete;
  }

  get automaticallySkipCondition(): Expression<'boolean'> {
    return this.definition.automaticallySkip;
  }

  get automaticallyFailCondition(): Expression<'boolean'> {
    return this.definition.automaticallyFail;
  }

  public get status() {
    return this.statusHistory[this.statusHistory.length - 1].status;
  }

  public get activationId() {
    const lastStatus = this.statusHistory[this.statusHistory.length - 1];
    if (!isActiveStepInstanceStatus(lastStatus)) {
      return undefined;
    }
    return lastStatus.activationId;
  }

  public get formSubmissionForLastActivation() {
    // TODO: figure out if we're polyfilling Array.prototype.findLast and such and can update to use that
    const statusHistoryReversed = [...this.statusHistory].reverse();

    const lastActiveStatus = statusHistoryReversed.find(isActiveStepInstanceStatus);
    if (lastActiveStatus == null) {
      return undefined;
    }

    const formSubmissionsReversed = [...this.formSubmissions].reverse();

    return formSubmissionsReversed.find((submission) => submission.stepActivationId === lastActiveStatus.activationId);
  }

  public get statusLastUpdated() {
    return this.statusHistory[this.statusHistory.length - 1].timestamp;
  }

  public get statusWasManual() {
    return this.statusHistory[this.statusHistory.length - 1].manual;
  }

  public get assignment(): AssignmentInstance | undefined {
    const currentStatus = this.statusHistory[this.statusHistory.length - 1];
    if (!isActiveStepInstanceStatus(currentStatus)) {
      return undefined;
    }

    const reversedAssignmentHistory = [...this.assignmentHistory].reverse();

    return reversedAssignmentHistory.find((assignment) => assignment.activationId === currentStatus.activationId);
  }
}
